import { useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackClassName?: string;
  fallbackLabel?: string;
  /**
   * Show the centered placeholder image icon in the empty/failed state.
   * Default true (right for admin grids/product slots that prompt "set an
   * image"). Pass false for decorative slots like the hero, where an empty
   * src should look like an intentional themed background, not a broken image.
   */
  fallbackIcon?: boolean;
}

export default function ImageWithFallback({
  src,
  className,
  alt,
  fallbackClassName,
  fallbackLabel,
  fallbackIcon = true,
  ...props
}: ImageWithFallbackProps) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center bg-gradient-to-br from-sage-100 via-sage-50 to-surface-muted',
          className,
          fallbackClassName,
        )}
        role="img"
        aria-label={alt || fallbackLabel || 'No image set'}
      >
        {fallbackIcon && <ImageIcon className="h-10 w-10 text-sage-300" aria-hidden="true" />}
        {fallbackLabel && (
          <span className="mt-2 text-sm text-sage-400">{fallbackLabel}</span>
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
      {...props}
    />
  );
}
